<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import AdminNavigation from '@/components/admin/AdminNavigation.vue'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'
import { Loader2, RefreshCw, AlertCircle, ChevronDown } from 'lucide-vue-next'
import { API, HOST, HOST_TULUM } from '@/utils/http'
import { formatDate, formatQuota, statusGroup, STATUS_GROUP_LABELS, LAYER_TYPE_LABELS, MIN_CAPTURE_DAYS, DAY_MS } from './format'
import type { PlanetSubscription, TaskingContract, PlanetProduct, Organization, PendingLayer, OrgQuota, PricingEstimate } from './types'
import AdminLayersMap from './AdminLayersMap.vue'
import PendingLayerCard from './PendingLayerCard.vue'
import HistoryLayerCard from './HistoryLayerCard.vue'

const store = useStore()
const router = useRouter()
const member = computed(() => store.getters.getMember)
const map = computed(() => store.getters['geo/getMap'])

// Organizations state
const organizations = ref<Organization[]>([])
const organizationsLoading = ref(false)
const selectedOrgId = ref<string | null>(null)

// Products data state
const subscriptions = ref<PlanetSubscription[]>([])
const taskingContracts = ref<TaskingContract[]>([])
const products = ref<PlanetProduct[]>([])
const loading = ref(false)
const error = ref<string | null>(null)

// Pending layers state
const pendingLayers = ref<PendingLayer[]>([])
const historyLayers = ref<PendingLayer[]>([])
const pendingLoading = ref(false)
const historyLoading = ref(false)
const pendingError = ref<string | null>(null)
const historyError = ref<string | null>(null)
const rejectDialogOpen = ref(false)
const rejectingLayer = ref<PendingLayer | null>(null)
const rejectReason = ref('')
const resetDialogOpen = ref(false)
const resettingLayer = ref<PendingLayer | null>(null)
const resetError = ref<string | null>(null)
const processingAction = ref<string | null>(null)

// Org quota cache for pending layer decision context
const orgQuotas = ref<Record<number, OrgQuota>>({})
const orgQuotaLoading = ref<Set<number>>(new Set())

// Live Planet pricing per pending tasking layer (credits)
const pricingByCode = ref<Record<string, PricingEstimate>>({})

// Map selection + filters
const selectedCode = ref<string | null>(null)
const pendingOrgFilter = ref<string>('all')
const historyOrgFilter = ref<string>('all')
const historyTypeFilter = ref<string>('all')
const historyStatusFilter = ref<string>('all')

// Main section tab (Layers or Products)
const mainSection = ref('layers')

// Sub-tabs
const layersTab = ref('pending')
const productsTab = ref('downloads')

// Track expanded state
const expandedItems = ref<Set<string>>(new Set())

const toggleExpanded = (id: string) => {
  if (expandedItems.value.has(id)) {
    expandedItems.value.delete(id)
  } else {
    expandedItems.value.add(id)
  }
}

const isExpanded = (id: string) => expandedItems.value.has(id)

// Computed filtered subscriptions by category
const downloadSubscriptions = computed(() =>
  subscriptions.value.filter(s =>
    s.plan_name?.toLowerCase().includes('skysat') ||
    s.plan_name?.toLowerCase().includes('ps_re') ||
    s.plan_name?.toLowerCase().includes('download') ||
    (!s.plan_name?.toLowerCase().includes('mosaic') &&
     !s.plan_name?.toLowerCase().includes('basemap') &&
     !s.plan_name?.toLowerCase().includes('stream') &&
     s.quota_sqkm !== null)
  )
)

const streamingSubscriptions = computed(() =>
  subscriptions.value.filter(s =>
    s.plan_name?.toLowerCase().includes('mosaic') ||
    s.plan_name?.toLowerCase().includes('basemap') ||
    s.plan_name?.toLowerCase().includes('stream') ||
    s.basemap_quad_quota !== null ||
    s.scene_quad_quota !== null
  )
)

const otherProducts = computed(() => products.value)

const fetchOrganizations = async () => {
  organizationsLoading.value = true
  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/admin/organizations`,
      params: { limit: 500 }
    })
    organizations.value = response.data
  } catch (err: any) {
    console.error('Error fetching organizations:', err)
  } finally {
    organizationsLoading.value = false
  }
}

const errorDetail = (err: any): string => {
  const detail = err?.response?.data?.detail
  if (Array.isArray(detail)) return detail.map(d => d?.msg ?? String(d)).join('; ')
  return detail || err?.message || 'Request failed'
}

const fetchOrgQuota = async (orgId: number, force = false) => {
  if (orgQuotaLoading.value.has(orgId) || (orgQuotas.value[orgId] && !force)) return
  orgQuotaLoading.value = new Set([...orgQuotaLoading.value, orgId])
  try {
    const [subs, contracts, products] = await Promise.allSettled([
      API({ method: 'GET', url: `${HOST_TULUM}/planet/${orgId}/subscriptions` }),
      API({ method: 'GET', url: `${HOST_TULUM}/planet/${orgId}/tasking-contracts` }),
      API({ method: 'GET', url: `${HOST_TULUM}/planet/${orgId}/products` }),
    ])
    const value = (r: PromiseSettledResult<any>, key: string) => r.status === 'fulfilled' ? (r.value.data?.[key] ?? []) : []
    const reason = (r: PromiseSettledResult<any>) => r.status === 'rejected' ? errorDetail(r.reason) : undefined
    orgQuotas.value = {
      ...orgQuotas.value,
      [orgId]: {
        subscriptions: value(subs, 'subscriptions'),
        taskingContracts: value(contracts, 'contracts'),
        products: value(products, 'products'),
        error: [subs, contracts, products].every(r => r.status === 'rejected' && r.reason?.response?.status === 400),
        errors: { subscriptions: reason(subs), taskingContracts: reason(contracts), products: reason(products) },
      },
    }
  } finally {
    const next = new Set(orgQuotaLoading.value)
    next.delete(orgId)
    orgQuotaLoading.value = next
  }
}

const HOUR_MS = 60 * 60 * 1000

const getUsagePercentage = (used: number | null, total: number | null): string => {
  if (used === null || total === null || total === 0) return '--'
  return `${Math.round((used / total) * 100)}%`
}
const windowErrorByCode = ref<Record<string, string>>({})

const pricingRequestSeq: Record<string, number> = {}

const setPricing = (code: string, value: PricingEstimate) => {
  pricingByCode.value = { ...pricingByCode.value, [code]: value }
}

// Tasking contracts are billed in credits, so re-quote the stored AOI through
// Planet's pricing endpoint. Planet rejects windows that start in the past, so
// a stale start is clamped to now + 1h and flagged as re-quoted.
const fetchPricing = async (layer: PendingLayer) => {
  if (layer.layer_type !== 'tasking' || !layer.geometry || !layer.data?.start_time || !layer.data?.end_time) return
  const now = Date.now()
  let start = new Date(layer.data.start_time)
  let requoted = false
  if (start.getTime() < now + HOUR_MS) {
    start = new Date(now + HOUR_MS)
    requoted = true
  }
  if (new Date(layer.data.end_time).getTime() - start.getTime() < MIN_CAPTURE_DAYS * DAY_MS) {
    setPricing(layer.code, { elapsed: true })
    return
  }
  const seq = (pricingRequestSeq[layer.code] ?? 0) + 1
  pricingRequestSeq[layer.code] = seq
  setPricing(layer.code, { loading: true })
  try {
    const response = await API({
      method: 'POST',
      url: `${HOST_TULUM}/tasking/${layer.org_id}/pricing`,
      data: {
        geometry: layer.geometry,
        order_type: layer.data.order_type || 'IMAGE',
        start_time: start.toISOString(),
        end_time: layer.data.end_time,
        sat_elevation_angle_min: layer.data.sat_elevation_angle_min ?? 60,
      },
    })
    if (pricingRequestSeq[layer.code] !== seq) return
    setPricing(layer.code, {
      estimated_cost: response.data.estimated_cost ?? null,
      units: response.data.units || 'CREDITS',
      area_km2: response.data.area_km2 ?? null,
      requoted,
    })
  } catch (err: any) {
    if (pricingRequestSeq[layer.code] !== seq) return
    setPricing(layer.code, { error: err.response?.data?.detail || 'Pricing unavailable' })
  }
}

const fetchPendingLayers = async () => {
  pendingLoading.value = true
  pendingError.value = null

  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/admin/pending-layers`
    })
    pendingLayers.value = response.data
    const uniqueOrgIds = [...new Set<number>(response.data.map((l: PendingLayer) => l.org_id))]
    uniqueOrgIds.forEach(orgId => fetchOrgQuota(orgId, true))
    response.data.forEach((l: PendingLayer) => fetchPricing(l))
  } catch (err: any) {
    console.error('Error fetching pending layers:', err)
    pendingError.value = err.response?.data?.detail || 'Failed to fetch pending layers'
  } finally {
    pendingLoading.value = false
  }
}

const fetchHistoryLayers = async () => {
  historyLoading.value = true
  historyError.value = null

  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/admin/layers-history`
    })
    historyLayers.value = response.data
  } catch (err: any) {
    console.error('Error fetching layer history:', err)
    historyError.value = err.response?.data?.detail || 'Failed to fetch layer history'
  } finally {
    historyLoading.value = false
  }
}

const approveLayer = async (layer: PendingLayer) => {
  processingAction.value = layer.code
  try {
    await API({
      method: 'POST',
      url: `${HOST}/admin/layers/${layer.code}/approve`
    })
    pendingLayers.value = pendingLayers.value.filter(l => l.code !== layer.code)
    fetchHistoryLayers()
    fetchOrgQuota(layer.org_id, true)
  } catch (err: any) {
    console.error('Error approving layer:', err)
    pendingError.value = err.response?.data?.detail || 'Failed to approve layer'
  } finally {
    processingAction.value = null
  }
}

const updateCaptureWindow = async (layer: PendingLayer, window: { start: Date; end: Date }) => {
  processingAction.value = layer.code
  try {
    const response = await API({
      method: 'PATCH',
      url: `${HOST}/admin/layers/${layer.code}/capture-window`,
      data: { start_time: window.start.toISOString(), end_time: window.end.toISOString() }
    })
    pendingLayers.value = pendingLayers.value.map(l => l.code === layer.code ? { ...l, ...response.data } : l)
    windowErrorByCode.value = { ...windowErrorByCode.value, [layer.code]: '' }
    fetchPricing(pendingLayers.value.find(l => l.code === layer.code)!)
  } catch (err: any) {
    console.error('Error updating capture window:', err)
    windowErrorByCode.value = { ...windowErrorByCode.value, [layer.code]: errorDetail(err) }
  } finally {
    processingAction.value = null
  }
}

const openRejectDialog = (layer: PendingLayer) => {
  rejectingLayer.value = layer
  rejectReason.value = ''
  rejectDialogOpen.value = true
}

const confirmReject = async () => {
  if (!rejectingLayer.value || !rejectReason.value.trim()) return

  processingAction.value = rejectingLayer.value.code
  try {
    await API({
      method: 'POST',
      url: `${HOST}/admin/layers/${rejectingLayer.value.code}/reject`,
      data: { reason: rejectReason.value.trim() }
    })
    pendingLayers.value = pendingLayers.value.filter(l => l.code !== rejectingLayer.value?.code)
    rejectDialogOpen.value = false
    rejectingLayer.value = null
    rejectReason.value = ''
    fetchHistoryLayers()
  } catch (err: any) {
    console.error('Error rejecting layer:', err)
    pendingError.value = err.response?.data?.detail || 'Failed to reject layer'
  } finally {
    processingAction.value = null
  }
}

const openResetDialog = (layer: PendingLayer) => {
  resettingLayer.value = layer
  resetError.value = null
  resetDialogOpen.value = true
}

const confirmReset = async () => {
  const layer = resettingLayer.value
  if (!layer) return

  processingAction.value = layer.code
  try {
    await API({
      method: 'POST',
      url: `${HOST}/admin/layers/${layer.code}/reset`
    })
    historyLayers.value = historyLayers.value.filter(l => l.code !== layer.code)
    resetDialogOpen.value = false
    resettingLayer.value = null
    fetchPendingLayers()
  } catch (err: any) {
    console.error('Error resetting layer:', err)
    resetError.value = err.response?.data?.detail || 'Failed to reset layer'
  } finally {
    processingAction.value = null
  }
}

const getOrgName = (orgId: number): string => {
  const org = organizations.value.find(o => o.organization_id === orgId)
  return org?.organization_name || `Org ${orgId}`
}

// ---- Layers list filtering / selection ----

const orgsIn = (layers: PendingLayer[]) =>
  [...new Set(layers.map(l => l.org_id))].map(id => ({ id, name: getOrgName(id) })).sort((a, b) => a.name.localeCompare(b.name))

const pendingOrgs = computed(() => orgsIn(pendingLayers.value))
const historyOrgs = computed(() => orgsIn(historyLayers.value))

const filteredPending = computed(() =>
  pendingLayers.value.filter(l => pendingOrgFilter.value === 'all' || String(l.org_id) === pendingOrgFilter.value))

const filteredHistory = computed(() =>
  historyLayers.value.filter(l =>
    (historyOrgFilter.value === 'all' || String(l.org_id) === historyOrgFilter.value) &&
    (historyTypeFilter.value === 'all' || l.layer_type === historyTypeFilter.value) &&
    (historyStatusFilter.value === 'all' || statusGroup(l.status) === historyStatusFilter.value)))

const visibleLayers = computed(() => layersTab.value === 'pending' ? filteredPending.value : filteredHistory.value)

const mapLayers = ref<InstanceType<typeof AdminLayersMap> | null>(null)

const zoomTo = (code: string) => {
  selectedCode.value = code
  mapLayers.value?.zoomTo(code)
}

const refreshQuotaContext = () => {
  orgQuotas.value = {}
  pricingByCode.value = {}
  const orgIds = [...new Set(pendingLayers.value.map(l => l.org_id))]
  orgIds.forEach(orgId => fetchOrgQuota(orgId, true))
  pendingLayers.value.forEach(l => fetchPricing(l))
}

// Keep a valid selection: default to the first visible card, drop it when the
// card disappears (approved/rejected/filtered out).
watch(visibleLayers, (layers) => {
  if (!layers.some(l => l.code === selectedCode.value)) {
    selectedCode.value = layers[0]?.code ?? null
  }
}, { immediate: true })

const fetchData = async () => {
  if (!selectedOrgId.value) {
    subscriptions.value = []
    taskingContracts.value = []
    products.value = []
    return
  }

  loading.value = true
  error.value = null

  try {
    const [subsResponse, taskingResponse, productsResponse] = await Promise.all([
      API({ method: 'GET', url: `${HOST_TULUM}/planet/${selectedOrgId.value}/subscriptions` }),
      API({ method: 'GET', url: `${HOST_TULUM}/planet/${selectedOrgId.value}/tasking-contracts` }),
      API({ method: 'GET', url: `${HOST_TULUM}/planet/${selectedOrgId.value}/products` })
    ])

    subscriptions.value = subsResponse.data.subscriptions || []
    taskingContracts.value = taskingResponse.data.contracts || []
    products.value = productsResponse.data.products || []

  } catch (err: any) {
    console.error('Error fetching data:', err)
    error.value = err.response?.data?.detail || 'Failed to fetch data'
  } finally {
    loading.value = false
  }
}

const handleOrgChange = (value: any) => {
  selectedOrgId.value = value
  expandedItems.value.clear()
  fetchData()
}

watch(() => member.value, (value) => {
  if (!value) return
  if (!value.is_super_user) {
    router.push({ name: 'Dashboard' })
  }
})

onMounted(() => {
  if (member.value && !member.value.is_super_user) {
    router.push({ name: 'Dashboard' })
    return
  }
  fetchOrganizations()
  fetchPendingLayers()
  fetchHistoryLayers()
})
</script>

<template>
  <div v-if="member && member.is_super_user" class="h-full flex flex-col">
    <AdminNavigation />

    <div class="flex-1 overflow-auto px-6 py-4">
      <h1 class="text-2xl font-bold mb-4">Planet</h1>

      <!-- Main Section Tabs -->
      <Tabs v-model="mainSection">
        <TabsList class="w-fit mb-4">
          <TabsTrigger value="layers">
            Layers
          </TabsTrigger>
          <TabsTrigger value="products">
            Products
          </TabsTrigger>
        </TabsList>

        <!-- Layers Section -->
        <TabsContent value="layers">
          <Tabs v-model="layersTab">
            <div class="flex items-center justify-between gap-4 mb-4 flex-wrap">
              <TabsList>
                <TabsTrigger value="pending">
                  Pending ({{ pendingLayers.length }})
                </TabsTrigger>
                <TabsTrigger value="history">
                  History ({{ historyLayers.length }})
                </TabsTrigger>
              </TabsList>

              <div class="flex items-center gap-2 flex-wrap">
                <AdminLayersMap v-if="map" ref="mapLayers" :map="map" :layers="visibleLayers" :selected-code="selectedCode" />
                <template v-if="layersTab === 'pending'">
                  <Select v-model="pendingOrgFilter">
                    <SelectTrigger class="w-56 h-8 text-sm"><SelectValue placeholder="All organizations" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All organizations</SelectItem>
                      <SelectItem v-for="org in pendingOrgs" :key="org.id" :value="String(org.id)">{{ org.name }}</SelectItem>
                    </SelectContent>
                  </Select>
                </template>
                <template v-else>
                  <Select v-model="historyOrgFilter">
                    <SelectTrigger class="w-56 h-8 text-sm"><SelectValue placeholder="All organizations" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All organizations</SelectItem>
                      <SelectItem v-for="org in historyOrgs" :key="org.id" :value="String(org.id)">{{ org.name }}</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select v-model="historyTypeFilter">
                    <SelectTrigger class="w-36 h-8 text-sm"><SelectValue placeholder="All types" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All types</SelectItem>
                      <SelectItem v-for="(label, type) in LAYER_TYPE_LABELS" :key="type" :value="type">{{ label }}</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select v-model="historyStatusFilter">
                    <SelectTrigger class="w-36 h-8 text-sm"><SelectValue placeholder="All statuses" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All statuses</SelectItem>
                      <SelectItem v-for="(label, group) in STATUS_GROUP_LABELS" :key="group" :value="group">{{ label }}</SelectItem>
                    </SelectContent>
                  </Select>
                </template>
                <Button
                  variant="outline"
                  size="sm"
                  @click="layersTab === 'pending' ? fetchPendingLayers() : fetchHistoryLayers()"
                  :disabled="layersTab === 'pending' ? pendingLoading : historyLoading"
                >
                  <RefreshCw :class="['h-4 w-4 mr-2', { 'animate-spin': layersTab === 'pending' ? pendingLoading : historyLoading }]" />
                  Refresh
                </Button>
              </div>
            </div>

            <div>
              <!-- Pending list -->
              <template v-if="layersTab === 'pending'">
                <p class="text-sm text-muted-foreground mb-3">Planet layers awaiting admin approval</p>

                <div v-if="pendingLoading" class="flex items-center justify-center py-8">
                  <Loader2 class="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
                <div v-else-if="pendingError" class="p-4 bg-destructive/10 border border-destructive/30 rounded-md">
                  <div class="flex items-center gap-2 text-destructive">
                    <AlertCircle class="h-5 w-5" />
                    <span>{{ pendingError }}</span>
                  </div>
                </div>
                <div v-else-if="filteredPending.length === 0" class="text-center py-8 text-muted-foreground">
                  No pending layers requiring approval
                </div>
                <div v-else class="space-y-2">
                  <PendingLayerCard
                    v-for="layer in filteredPending"
                    :key="layer.code"
                    :layer="layer"
                    :org-name="getOrgName(layer.org_id)"
                    :selected="selectedCode === layer.code"
                    :processing="processingAction === layer.code"
                    :quota="orgQuotas[layer.org_id]"
                    :quota-loading="orgQuotaLoading.has(layer.org_id) && !orgQuotas[layer.org_id]"
                    :pricing="pricingByCode[layer.code]"
                    :window-error="windowErrorByCode[layer.code]"
                    @select="selectedCode = layer.code"
                    @zoom="zoomTo(layer.code)"
                    @approve="approveLayer(layer)"
                    @update-window="updateCaptureWindow(layer, $event)"
                    @reject="openRejectDialog(layer)"
                  />
                </div>
              </template>

              <!-- History list -->
              <template v-else>
                <p class="text-sm text-muted-foreground mb-3">Approved and rejected Planet layers, with their current pipeline status</p>

                <div v-if="historyLoading" class="flex items-center justify-center py-8">
                  <Loader2 class="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
                <div v-else-if="historyError" class="p-4 bg-destructive/10 border border-destructive/30 rounded-md">
                  <div class="flex items-center gap-2 text-destructive">
                    <AlertCircle class="h-5 w-5" />
                    <span>{{ historyError }}</span>
                  </div>
                </div>
                <div v-else-if="filteredHistory.length === 0" class="text-center py-8 text-muted-foreground">
                  No layer history found
                </div>
                <div v-else class="space-y-2">
                  <HistoryLayerCard
                    v-for="layer in filteredHistory"
                    :key="layer.code"
                    :layer="layer"
                    :org-name="getOrgName(layer.org_id)"
                    :selected="selectedCode === layer.code"
                    :processing="processingAction === layer.code"
                    @select="selectedCode = layer.code"
                    @zoom="zoomTo(layer.code)"
                    @reset="openResetDialog(layer)"
                  />
                </div>
              </template>
            </div>
          </Tabs>
        </TabsContent>

        <!-- Products Section -->
        <TabsContent value="products">
          <!-- Organization Selector -->
          <div class="mb-4 flex items-center gap-4">
            <div class="w-80">
              <Select @update:model-value="handleOrgChange">
                <SelectTrigger>
                  <SelectValue placeholder="Select an organization" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem
                    v-for="org in organizations"
                    :key="org.organization_id"
                    :value="String(org.organization_id)"
                  >
                    {{ org.organization_name }}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              v-if="selectedOrgId"
              variant="outline"
              size="sm"
              @click="fetchData"
              :disabled="loading"
            >
              <RefreshCw :class="['h-4 w-4 mr-2', { 'animate-spin': loading }]" />
              Refresh
            </Button>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="flex items-center justify-center py-12">
            <Loader2 class="h-8 w-8 animate-spin text-muted-foreground" />
          </div>

          <!-- Error State -->
          <div v-else-if="error" class="p-4 bg-destructive/10 border border-destructive/30 rounded-md">
            <div class="flex items-center gap-2 text-destructive">
              <AlertCircle class="h-5 w-5" />
              <span>{{ error }}</span>
            </div>
          </div>

          <!-- Empty State -->
          <div v-else-if="!selectedOrgId" class="text-center py-12 text-muted-foreground">
            Select an organization to view Planet products
          </div>

          <!-- Products Tabs -->
          <Tabs v-else v-model="productsTab" class="h-[calc(100%-60px)] flex flex-col">
            <TabsList class="w-fit">
              <TabsTrigger value="downloads">
                Downloads ({{ downloadSubscriptions.length }})
              </TabsTrigger>
              <TabsTrigger value="streaming">
                Streaming ({{ streamingSubscriptions.length }})
              </TabsTrigger>
              <TabsTrigger value="tasking">
                Tasking ({{ taskingContracts.length }})
              </TabsTrigger>
              <TabsTrigger value="other">
                Other ({{ otherProducts.length }})
              </TabsTrigger>
            </TabsList>

            <!-- Downloads Tab -->
            <TabsContent value="downloads" class="mt-4">
              <div v-if="downloadSubscriptions.length === 0" class="text-center py-8 text-muted-foreground">
                No download subscriptions found
              </div>
              <div v-else class="space-y-2">
                <div
                  v-for="sub in downloadSubscriptions"
                  :key="`dl-${sub.id}`"
                  class="border rounded-lg bg-card"
                >
                  <Collapsible :open="isExpanded(`dl-${sub.id}`)">
                    <CollapsibleTrigger class="w-full" @click="toggleExpanded(`dl-${sub.id}`)">
                      <div class="flex items-center justify-between p-4 hover:bg-muted/50 cursor-pointer">
                        <div class="flex items-center gap-4 flex-1 min-w-0">
                          <ChevronDown :class="['h-4 w-4 text-muted-foreground transition-transform', { 'rotate-180': isExpanded(`dl-${sub.id}`) }]" />
                          <div class="flex-1 min-w-0">
                            <span class="font-medium truncate block">{{ sub.plan_name }}</span>
                            <span class="text-sm text-muted-foreground">ID: {{ sub.id }}</span>
                          </div>
                        </div>
                        <div class="flex items-center gap-8 text-sm">
                          <div class="text-right">
                            <span class="text-muted-foreground">Download Quota</span>
                            <div class="font-medium">{{ getUsagePercentage(sub.used_sqkm, sub.quota_sqkm) }}</div>
                          </div>
                          <div class="text-right min-w-[180px]">
                            <span class="text-muted-foreground">Contract Term</span>
                            <div class="font-medium">{{ formatDate(sub.start_time) }} - {{ formatDate(sub.end_time) }}</div>
                          </div>
                          <Badge :variant="sub.state === 'active' ? 'default' : 'secondary'" class="capitalize">
                            {{ sub.state }}
                          </Badge>
                        </div>
                      </div>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div class="px-4 pb-4 pt-0 border-t bg-muted/50">
                        <div class="grid grid-cols-2 gap-4 pt-4 text-sm">
                          <div>
                            <span class="text-muted-foreground">Quota (km²):</span>
                            <span class="ml-2 font-mono">{{ formatQuota(sub.quota_sqkm, 'km²') }}</span>
                          </div>
                          <div>
                            <span class="text-muted-foreground">Used (km²):</span>
                            <span class="ml-2 font-mono">{{ formatQuota(sub.used_sqkm, 'km²') }}</span>
                          </div>
                          <div v-if="sub.extra_fields" class="col-span-2">
                            <span class="text-muted-foreground block mb-2">Additional Fields:</span>
                            <pre class="text-xs bg-muted p-2 rounded overflow-auto max-h-32">{{ JSON.stringify(sub.extra_fields, null, 2) }}</pre>
                          </div>
                        </div>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                </div>
              </div>
            </TabsContent>

            <!-- Streaming Tab -->
            <TabsContent value="streaming" class="mt-4">
              <div v-if="streamingSubscriptions.length === 0" class="text-center py-8 text-muted-foreground">
                No streaming subscriptions found
              </div>
              <div v-else class="space-y-2">
                <div
                  v-for="sub in streamingSubscriptions"
                  :key="`str-${sub.id}`"
                  class="border rounded-lg bg-card"
                >
                  <Collapsible :open="isExpanded(`str-${sub.id}`)">
                    <CollapsibleTrigger class="w-full" @click="toggleExpanded(`str-${sub.id}`)">
                      <div class="flex items-center justify-between p-4 hover:bg-muted/50 cursor-pointer">
                        <div class="flex items-center gap-4 flex-1 min-w-0">
                          <ChevronDown :class="['h-4 w-4 text-muted-foreground transition-transform', { 'rotate-180': isExpanded(`str-${sub.id}`) }]" />
                          <div class="flex-1 min-w-0">
                            <span class="font-medium truncate block">{{ sub.plan_name }}</span>
                            <span class="text-sm text-muted-foreground">ID: {{ sub.id }}</span>
                          </div>
                        </div>
                        <div class="flex items-center gap-8 text-sm">
                          <div class="text-right">
                            <span class="text-muted-foreground">Basemap Tile Quota</span>
                            <div class="font-medium">{{ getUsagePercentage(sub.basemap_quad_used, sub.basemap_quad_quota) }}</div>
                          </div>
                          <div class="text-right">
                            <span class="text-muted-foreground">Scene Tile Quota</span>
                            <div class="font-medium">{{ getUsagePercentage(sub.scene_quad_used, sub.scene_quad_quota) }}</div>
                          </div>
                          <div class="text-right min-w-[180px]">
                            <span class="text-muted-foreground">Contract Term</span>
                            <div class="font-medium">{{ formatDate(sub.start_time) }} - {{ formatDate(sub.end_time) }}</div>
                          </div>
                          <Badge :variant="sub.state === 'active' ? 'default' : 'secondary'" class="capitalize">
                            {{ sub.state }}
                          </Badge>
                        </div>
                      </div>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div class="px-4 pb-4 pt-0 border-t bg-muted/50">
                        <div class="grid grid-cols-2 gap-4 pt-4 text-sm">
                          <div>
                            <span class="text-muted-foreground">Basemap Quad Quota:</span>
                            <span class="ml-2 font-mono">{{ sub.basemap_quad_quota?.toLocaleString() ?? '--' }}</span>
                          </div>
                          <div>
                            <span class="text-muted-foreground">Basemap Quad Used:</span>
                            <span class="ml-2 font-mono">{{ sub.basemap_quad_used?.toLocaleString() ?? '--' }}</span>
                          </div>
                          <div>
                            <span class="text-muted-foreground">Scene Quad Quota:</span>
                            <span class="ml-2 font-mono">{{ sub.scene_quad_quota?.toLocaleString() ?? '--' }}</span>
                          </div>
                          <div>
                            <span class="text-muted-foreground">Scene Quad Used:</span>
                            <span class="ml-2 font-mono">{{ sub.scene_quad_used?.toLocaleString() ?? '--' }}</span>
                          </div>
                          <div v-if="sub.extra_fields" class="col-span-2">
                            <span class="text-muted-foreground block mb-2">Additional Fields:</span>
                            <pre class="text-xs bg-muted p-2 rounded overflow-auto max-h-32">{{ JSON.stringify(sub.extra_fields, null, 2) }}</pre>
                          </div>
                        </div>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                </div>
              </div>
            </TabsContent>

            <!-- Tasking Tab -->
            <TabsContent value="tasking" class="mt-4">
              <div v-if="taskingContracts.length === 0" class="text-center py-8 text-muted-foreground">
                No tasking contracts found
              </div>
              <div v-else class="space-y-2">
                <div
                  v-for="contract in taskingContracts"
                  :key="`task-${contract.id}`"
                  class="border rounded-lg bg-card"
                >
                  <Collapsible :open="isExpanded(`task-${contract.id}`)">
                    <CollapsibleTrigger class="w-full" @click="toggleExpanded(`task-${contract.id}`)">
                      <div class="flex items-center justify-between p-4 hover:bg-muted/50 cursor-pointer">
                        <div class="flex items-center gap-4 flex-1 min-w-0">
                          <ChevronDown :class="['h-4 w-4 text-muted-foreground transition-transform', { 'rotate-180': isExpanded(`task-${contract.id}`) }]" />
                          <div class="flex-1 min-w-0">
                            <span class="font-medium truncate block">{{ contract.name || contract.sfdc }}</span>
                            <span class="text-sm text-muted-foreground">{{ contract.sfdc }}</span>
                          </div>
                        </div>
                        <div class="flex items-center gap-8 text-sm">
                          <div class="text-right">
                            <span class="text-muted-foreground">Quota</span>
                            <div class="font-medium">{{ contract.quota_limit?.toLocaleString() }} {{ contract.quota_units }}</div>
                          </div>
                          <div class="text-right">
                            <span class="text-muted-foreground">Used</span>
                            <div class="font-medium">{{ contract.quota_used?.toLocaleString() ?? 0 }} {{ contract.quota_units }}</div>
                          </div>
                          <div class="text-right min-w-[180px]">
                            <span class="text-muted-foreground">Contract Term</span>
                            <div class="font-medium">{{ formatDate(contract.start_date) }} - {{ formatDate(contract.expiration_date) }}</div>
                          </div>
                          <Badge :variant="contract.state === 'ACTIVE' ? 'default' : 'secondary'" class="capitalize">
                            {{ contract.state?.toLowerCase() }}
                          </Badge>
                        </div>
                      </div>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div class="px-4 pb-4 pt-0 border-t bg-muted/50">
                        <div class="grid grid-cols-2 gap-4 pt-4 text-sm">
                          <div>
                            <span class="text-muted-foreground">Remaining:</span>
                            <span class="ml-2 font-mono text-green-600 dark:text-green-400">{{ contract.quota_remaining?.toLocaleString() ?? '--' }} {{ contract.quota_units }}</span>
                          </div>
                          <div>
                            <span class="text-muted-foreground">Usage:</span>
                            <span class="ml-2 font-mono">{{ getUsagePercentage(contract.quota_used, contract.quota_limit) }}</span>
                          </div>
                          <div>
                            <span class="text-muted-foreground">Assured Quota:</span>
                            <span class="ml-2 font-mono">{{ contract.assured_quota_limit?.toLocaleString() ?? 0 }} {{ contract.quota_units }}</span>
                          </div>
                          <div>
                            <span class="text-muted-foreground">Overage:</span>
                            <span class="ml-2 font-mono">{{ contract.overage ? 'Yes' : 'No' }}</span>
                          </div>
                          <div v-if="contract.extra_fields" class="col-span-2">
                            <span class="text-muted-foreground block mb-2">Additional Fields:</span>
                            <pre class="text-xs bg-muted p-2 rounded overflow-auto max-h-32">{{ JSON.stringify(contract.extra_fields, null, 2) }}</pre>
                          </div>
                        </div>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                </div>
              </div>
            </TabsContent>

            <!-- Other Products Tab -->
            <TabsContent value="other" class="mt-4">
              <div v-if="otherProducts.length === 0" class="text-center py-8 text-muted-foreground">
                No other products found
              </div>
              <div v-else class="space-y-2">
                <div
                  v-for="product in otherProducts"
                  :key="`prod-${product.id}`"
                  class="border rounded-lg bg-card"
                >
                  <Collapsible :open="isExpanded(`prod-${product.id}`)">
                    <CollapsibleTrigger class="w-full" @click="toggleExpanded(`prod-${product.id}`)">
                      <div class="flex items-center justify-between p-4 hover:bg-muted/50 cursor-pointer">
                        <div class="flex items-center gap-4 flex-1 min-w-0">
                          <ChevronDown :class="['h-4 w-4 text-muted-foreground transition-transform', { 'rotate-180': isExpanded(`prod-${product.id}`) }]" />
                          <div class="flex-1 min-w-0">
                            <span class="font-medium truncate block">{{ product.title || product.name }}</span>
                            <span class="text-sm text-muted-foreground">{{ product.product_family }}</span>
                          </div>
                        </div>
                        <div class="flex items-center gap-8 text-sm">
                          <div class="text-right">
                            <span class="text-muted-foreground">Usage</span>
                            <div class="font-medium">
                              {{ product.unlimited_quota ? 'Unlimited' : getUsagePercentage(product.quota_used, product.quota_total) }}
                            </div>
                          </div>
                          <Badge variant="default">Active</Badge>
                        </div>
                      </div>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div class="px-4 pb-4 pt-0 border-t bg-muted/50">
                        <div class="grid grid-cols-2 gap-4 pt-4 text-sm">
                          <div v-if="product.description" class="col-span-2">
                            <span class="text-muted-foreground">Description:</span>
                            <p class="mt-1">{{ product.description }}</p>
                          </div>
                          <div v-if="!product.unlimited_quota">
                            <span class="text-muted-foreground">Quota:</span>
                            <span class="ml-2 font-mono">{{ formatQuota(product.quota_total, product.quota_units) }}</span>
                          </div>
                          <div v-if="!product.unlimited_quota">
                            <span class="text-muted-foreground">Used:</span>
                            <span class="ml-2 font-mono">{{ formatQuota(product.quota_used, product.quota_units) }}</span>
                          </div>
                          <div v-if="!product.unlimited_quota">
                            <span class="text-muted-foreground">Remaining:</span>
                            <span class="ml-2 font-mono text-green-600 dark:text-green-400">{{ formatQuota(product.quota_remaining, product.quota_units) }}</span>
                          </div>
                          <div v-if="product.extra_fields" class="col-span-2">
                            <span class="text-muted-foreground block mb-2">Additional Fields:</span>
                            <pre class="text-xs bg-muted p-2 rounded overflow-auto max-h-32">{{ JSON.stringify(product.extra_fields, null, 2) }}</pre>
                          </div>
                        </div>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </TabsContent>
      </Tabs>
    </div>

    <!-- Rejection Dialog -->
    <Dialog v-model:open="rejectDialogOpen">
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reject Layer</DialogTitle>
          <DialogDescription>
            Please provide a reason for rejecting this layer. This will be visible to the user.
          </DialogDescription>
        </DialogHeader>
        <div class="py-4">
          <Textarea
            v-model="rejectReason"
            placeholder="Enter rejection reason..."
            class="min-h-[100px]"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" @click="rejectDialogOpen = false">Cancel</Button>
          <Button
            variant="destructive"
            @click="confirmReject"
            :disabled="!rejectReason.trim() || processingAction !== null"
          >
            <Loader2 v-if="processingAction" class="h-4 w-4 mr-2 animate-spin" />
            Reject Layer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <!-- Reset Dialog -->
    <Dialog v-model:open="resetDialogOpen">
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reset to pending</DialogTitle>
          <DialogDescription>
            This moves "{{ resettingLayer?.layer_name || 'Unnamed Layer' }}" back to the pending queue. Previous approval, error and Planet details are cleared; a tasking capture window that is no longer valid is moved to the next 14 days. Review its parameters and approve it again.
          </DialogDescription>
        </DialogHeader>
        <div v-if="resettingLayer?.planet_id" class="text-sm text-amber-600 dark:text-amber-400">
          The cancelled Planet tasking order ({{ resettingLayer.planet_id }}) stays cancelled. Approving again will create a new one.
        </div>
        <div v-if="resetError" class="flex items-center gap-2 text-sm text-destructive">
          <AlertCircle class="h-4 w-4 shrink-0" />
          {{ resetError }}
        </div>
        <DialogFooter>
          <Button variant="outline" @click="resetDialogOpen = false">Cancel</Button>
          <Button @click="confirmReset" :disabled="processingAction !== null">
            <Loader2 v-if="processingAction" class="h-4 w-4 mr-2 animate-spin" />
            Reset to pending
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </div>
</template>
